import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { ElectionData, Candidate } from '../types';
import { Check, AlertCircle, ArrowRight } from 'lucide-react';
import { setSiteMeta } from '../utils/meta';

export const CastVotePage: React.FC = () => {
  const { voter } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<ElectionData>({});
  const [selections, setSelections] = useState<Record<string, string | string[]>>({});
  const [loading, setLoading] = useState(true);
  const [errorPrompt, setErrorPrompt] = useState('');

  useEffect(() => {
    if (!voter) {
      navigate('/');
      return;
    }

    const currentSync = api.getElectionSettingsSync();
    setSiteMeta({ election: currentSync, pageTitle: 'Official Ballot' });

    const loadData = async () => {
      setLoading(true);

      // Verify election schedule window
      const info = await api.getElectionInfo();
      setSiteMeta({ election: info, pageTitle: 'Official Ballot' });
      const now = new Date();
      if (info.startTime && now < new Date(info.startTime)) {
        navigate('/error/votingnotstarted');
        return;
      }
      if (info.endTime && now > new Date(info.endTime)) {
        navigate('/error/votingended');
        return;
      }
      if (info.status === 'paused' || info.status === 'draft') {
        navigate('/error/votingnotstarted');
        return;
      }

      const electionData = await api.getElectionData(voter.level);
      setData(electionData);

      // Restore any cached selections
      const savedSelections: Record<string, string | string[]> = {};
      for (const key of Object.keys(electionData)) {
        const val = localStorage.getItem(`ballot_${key}`);
        if (val) {
          try {
            savedSelections[key] = JSON.parse(val);
          } catch {
            savedSelections[key] = val;
          }
        }
      }
      setSelections(savedSelections);
      setLoading(false);
    };

    loadData();
  }, [voter, navigate]);

  const handleSingleSelect = (postKey: string, candidateId: string) => {
    setSelections((prev) => ({ ...prev, [postKey]: candidateId }));
    localStorage.setItem(`ballot_${postKey}`, candidateId);
    setErrorPrompt('');
  };

  const handleMultiSelect = (postKey: string, candidateId: string, maxChoices: number) => {
    const current = (selections[postKey] as string[]) || [];
    let updated: string[];

    if (current.includes(candidateId)) {
      updated = current.filter((id) => id !== candidateId);
    } else {
      if (current.length >= maxChoices) {
        alert(`You can only select up to ${maxChoices} candidates for this position.`);
        return;
      }
      updated = [...current, candidateId];
    }

    setSelections((prev) => ({ ...prev, [postKey]: updated }));
    localStorage.setItem(`ballot_${postKey}`, JSON.stringify(updated));
    setErrorPrompt('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Check that every position has at least one choice
    const unselectedPositions = Object.keys(data).filter((postKey) => {
      const sel = selections[postKey];
      if (!sel) return true;
      if (Array.isArray(sel) && sel.length === 0) return true;
      return false;
    });

    if (unselectedPositions.length > 0) {
      const missingNames = unselectedPositions.map((k) => data[k].name).join(', ');
      setErrorPrompt(`Please make a selection for all positions: ${missingNames}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // All selected -> proceed to review
    navigate('/vote/verify');
  };

  if (!voter) return null;

  return (
    <div className="dashboard-layout" style={{ minHeight: '100vh', backgroundColor: '#FAFAFA' }}>
      <Sidebar />

      <main className="dashboard-main-content">
        <div style={{ marginBottom: '32px' }} className="ballot-page-header">
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#FF9900', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Official Ballot
          </div>
          <h1 style={{ fontSize: '32px', fontWeight: 800, color: '#171619', marginTop: '6px' }}>
            Cast Your Vote
          </h1>
          <p style={{ color: '#6B7280', fontSize: '15px', marginTop: '6px' }}>
            Select your preferred candidates below. Positions filtered according to your accreditation ({voter.level} Level).
          </p>
        </div>

        {errorPrompt && (
          <div
            style={{
              padding: '16px 20px',
              backgroundColor: '#FEF2F2',
              border: '1.5px solid #F87171',
              borderRadius: '12px',
              color: '#B91C1C',
              fontWeight: 600,
              fontSize: '14px',
              marginBottom: '32px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <AlertCircle size={20} style={{ flexShrink: 0 }} />
            <span>{errorPrompt}</span>
          </div>
        )}

        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#8E8E93', fontSize: '16px' }}>
            Loading candidate ballot...
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              {Object.entries(data).map(([postKey, post]) => {
                const maxChoices = post.max || 1;
                const isMulti = maxChoices > 1;
                const currentSelection = selections[postKey];

                return (
                  <div key={postKey} className="cv-post-card">
                    <div className="cv-post-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', borderBottom: '1px solid #F3F4F6', paddingBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                      <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#171619', margin: 0 }}>
                        {post.name}
                      </h2>
                      {isMulti && (
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#FF9900', backgroundColor: '#FFFBEB', padding: '4px 12px', borderRadius: '20px' }}>
                          Select up to {maxChoices} (Selected: {Array.isArray(currentSelection) ? currentSelection.length : 0})
                        </span>
                      )}
                    </div>

                    {/* Candidate Cards Grid */}
                    <div className="candidate-grid">
                      {post.candidates.map((cand: Candidate) => {
                        const isSelected = isMulti
                          ? Array.isArray(currentSelection) && currentSelection.includes(cand.id)
                          : currentSelection === cand.id;

                        return (
                          <div
                            key={cand.id}
                            className={`candidate-card ${isSelected ? 'selected' : ''}`}
                            onClick={() => {
                              if (isMulti) {
                                handleMultiSelect(postKey, cand.id, maxChoices);
                              } else {
                                handleSingleSelect(postKey, cand.id);
                              }
                            }}
                          >
                            {/* Selection Radio/Checkbox Badge */}
                            <div
                              className="candidate-select-indicator"
                              style={{
                                borderRadius: isMulti ? '6px' : '50%',
                              }}
                            >
                              {isSelected && <Check size={13} color="#171619" strokeWidth={3} />}
                            </div>

                            {/* Centered Avatar (Legacy Alignment & Focus) */}
                            <div className="candidate-avatar-wrap">
                              <img
                                src={cand.image}
                                alt={cand.name}
                                className="candidate-avatar"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/assets/images/indecisive.png';
                                }}
                              />
                            </div>

                            {/* Centered Info & Typography */}
                            <div className="candidate-info">
                              <div className="candidate-name">
                                {cand.name}
                              </div>
                              {cand.nickname && (
                                <div className="candidate-nickname">
                                  "{cand.nickname}"
                                </div>
                              )}
                              <div className="candidate-role-badge">
                                {cand.isAbstain ? 'Ballot Option' : 'Candidate'}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bottom Actions */}
            <div style={{ marginTop: '40px', display: 'flex' }} className="ballot-page-actions">
              <button
                type="submit"
                className="btn-primary"
                style={{ maxWidth: '340px', width: '100%', height: '54px', fontSize: '16px' }}
              >
                <span>Verify Choices & Submit</span>
                <ArrowRight size={20} />
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
};
